import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // Navigation items
  const navItems = [
    { id: "home", label: "Home" },
    { id: "about", label: "About" },
    { id: "services", label: "Services" },
    { id: "projects", label: "Projects" },
    { id: "service-area", label: "Service Area" },
    { id: "contact", label: "Contact" },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[hsl(var(--background))] ${
          scrolled
            ? "bg-[hsl(var(--navy))]/95 backdrop-blur-md shadow-xl py-2"
            : "py-3"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
          {/* Logo */}
          <button
            onClick={() => scrollTo("home")}
            className="flex items-center gap-3 focus-ring rounded"
          >
            <div className="flex items-center justify-center w-11 h-11 rounded-sm font-heading text-2xl text-white leading-none bg-[#194d32]">
              NCC
            </div>
            <div className="hidden sm:block text-left leading-tight">
              <div className="font-heading text-lg tracking-wide leading-none text-[#646b76]">
                NATURALS CONCRETE
              </div>
              <div className="font-heading text-sm tracking-[0.2em] leading-none mt-0.5 text-[#194d32]">
                CONSTRUCTION
              </div>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="px-3 py-2 text-sm font-semibold hover:text-[hsl(var(--accent))] transition-colors uppercase tracking-wider focus-ring rounded text-[#646b76]/[0.8]"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Phone */}
            <a
              href="tel:+14794227180"
              onClick={() => {
                if (typeof window.gtag === "function") {
                  window.gtag("event", "contact_phone", {
                    event_category: "engagement",
                    event_label: "Header Phone",
                  });
                }
              }}
              className="hidden md:flex items-center gap-2 text-white font-bold hover:text-[hsl(var(--accent))] transition-colors focus-ring rounded"
            >
              <Phone size={18} className="text-[#646b76]" />
              <span className="text-sm text-[#646b76]">479-422-7180</span>
            </a>

            {/* CTA */}
            <button
              onClick={() => {
                if (typeof window.gtag === "function") {
                  window.gtag("event", "request_estimate", {
                    event_category: "engagement",
                    event_label: "Header CTA",
                  });
                }
                scrollTo("quote");
              }}
              className="hidden sm:block hover:bg-[hsl(var(--accent))]/90 text-white font-heading text-base tracking-wider px-5 py-2.5 rounded-sm transition-all hover:shadow-lg hover:shadow-[hsl(var(--accent))]/30 focus-ring bg-[#194e33]"
            >
              Get a Free Estimate
            </button>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden text-white p-2 focus-ring rounded"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-[hsl(var(--navy))]/95 backdrop-blur-md pt-20 px-6 animate-fade-in">
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  className="text-left text-white text-2xl font-heading tracking-wider py-3 border-b border-white/10 hover:text-[hsl(var(--accent))] transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </nav>
            <a
              href="tel:+14794227180"
              onClick={() => {
                if (typeof window.gtag === "function") {
                  window.gtag("event", "contact_phone", {
                    event_category: "engagement",
                    event_label: "Mobile Menu Phone",
                  });
                }
              }}
              className="flex items-center justify-center gap-2 mt-6 text-white text-xl font-heading tracking-wider"
            >
              <Phone size={20} /> 479-422-7180
            </a>
            <button
              onClick={() => {
                if (typeof window.gtag === "function") {
                  window.gtag("event", "request_estimate", {
                    event_category: "engagement",
                    event_label: "Mobile Menu CTA",
                  });
                }
                scrollTo("quote");
              }}
              className="w-full mt-6 bg-[hsl(var(--accent))] text-white font-heading text-xl tracking-wider py-4 rounded-sm"
            >
              Get a Free Estimate
            </button>
          </div>
        </div>
      )}
    </>
  );
}